import type { TranslationBundle } from '@bms/common';

/**
 * The `controls` namespace: the room/channel controls (thermostat, dimmer,
 * switch, contact, temperature), the room cards, and the dashboard's
 * building/floor navigation layouts. Keys are read as `controls.<key>`
 * through `useTranslation()`.
 */
export const controls: TranslationBundle = {
  en: {
    floorLabel: 'Floor {{code}}',
    otherGroup: 'Other',
    backToDashboardAria: 'Back to dashboard',
    groupAria_one: '{{name}} group, {{count}} control',
    groupAria_other: '{{name}} group, {{count}} controls',
    updateFailed: 'Failed to update {{name}}',
    noReadableProperties: 'No readable properties',
    noReadablePropertiesFor: '{{type}} — no readable properties',
    stepper: {
      decrease: 'Decrease {{label}}',
      increase: 'Increase {{label}}',
    },
    state: {
      on: 'on',
      off: 'off',
      onCap: 'On',
      offCap: 'Off',
    },
    toggleAria: 'Toggle {{name}}',
    contact: {
      detected: 'Detected',
      clear: 'Clear',
      open: 'Open',
      closed: 'Closed',
      enable: 'Enable',
      disable: 'Disable',
      stateAria: '{{name}}: {{state}}',
      toggleAria: '{{action}} {{name}}',
    },
    dimmer: {
      stateAria: '{{name}} {{state}}',
      actual: '· Actual {{value}}',
      hardwareReports: '· Hardware reports {{state}}',
      noState: 'No state reported',
      commanded: '· Command sent, no state reported',
      fault: '· Fault',
      levelLabel: '{{name}} level',
      levelReadout: 'Level {{level}}',
    },
    switch: {
      stateAria: '{{name}} {{state}}',
      locked: 'Locked',
      timer: 'Timer',
      mismatch: 'Mismatch',
    },
    temperature: {
      noData: 'no data',
      probeErrorSuffix: ', probe error',
      overheatSuffix: ', overheat alert',
      overcoolSuffix: ', overcool alert',
      aria: '{{name}}: {{value}}{{alertSuffix}}',
      probeError: 'Probe Error',
      overheat: 'Overheat',
      overcool: 'Overcool',
    },
    thermostat: {
      setpoint: 'Setpoint',
      setpointLower: 'setpoint',
      mode: 'Mode',
      fan: 'Fan',
      offset: 'Offset',
      offsetLower: 'offset',
      unknown: 'Unknown',
      resetOffset: 'Reset offset',
      windowInterlock: 'Window interlock',
      windowOpen: 'Window open',
      off: 'Off',
      arrow: '→',
      turnOffAria: 'Turn {{name}} off',
      turnOnAria: 'Turn {{name}} on',
    },
    dashboard: {
      floorCount_one: '{{count}} floor',
      floorCount_other: '{{count}} floors',
      roomCount_one: '{{count}} room',
      roomCount_other: '{{count}} rooms',
      buildingSummary: '{{floors}} · {{rooms}}',
      unassigned: 'Unassigned',
      allRooms: 'All Rooms',
      buildingHierarchyAria: 'Building hierarchy',
      toggleBuildingAria: 'Toggle {{name}}',
      navigate: 'Navigate',
      roomListAria: 'Room list',
      noRoomsHere: 'No rooms in this location.',
      buildingsCrumb: 'Buildings',
      searchRoomsAria: 'Search rooms',
    },
    roomCard: {
      statusActive: 'Active',
      statusSetup: 'Setup',
      statusMaint: 'Maint.',
      statusOff: 'Off',
      alarmActiveSuffix: ', alarm active',
      compactAria: '{{name}} — {{status}}{{alarmSuffix}}',
      residentialAria: '{{name}} — {{channels}}',
      channelCount_one: '{{count}} channel',
      channelCount_other: '{{count}} channels',
      noChannelsMapped: 'No channels mapped',
      doNotDisturb: 'Do Not Disturb',
      makeUpRoom: 'Make Up Room',
      offline: 'offline',
      degrees: '{{value}}°',
      degreesC: '{{value}}°C',
    },
    roomParams: {
      changedCount_one: '{{count}} changed',
      changedCount_other: '{{count}} changed',
      resetChanges: 'Reset changes',
    },
  },
  hr: {
    floorLabel: 'Kat {{code}}',
    otherGroup: 'Ostalo',
    backToDashboardAria: 'Natrag na nadzornu ploču',
    groupAria_one: 'Skupina {{name}}, {{count}} kontrola',
    groupAria_few: 'Skupina {{name}}, {{count}} kontrole',
    groupAria_other: 'Skupina {{name}}, {{count}} kontrola',
    updateFailed: 'Ažuriranje nije uspjelo: {{name}}',
    noReadableProperties: 'Nema svojstava za čitanje',
    noReadablePropertiesFor: '{{type}} — nema svojstava za čitanje',
    stepper: {
      decrease: 'Smanji {{label}}',
      increase: 'Povećaj {{label}}',
    },
    state: {
      on: 'uključeno',
      off: 'isključeno',
      onCap: 'Uključeno',
      offCap: 'Isključeno',
    },
    toggleAria: 'Uklj./isklj. {{name}}',
    contact: {
      detected: 'Otkriveno',
      clear: 'Nema pokreta',
      open: 'Otvoreno',
      closed: 'Zatvoreno',
      enable: 'Omogući',
      disable: 'Onemogući',
      stateAria: '{{name}}: {{state}}',
      toggleAria: '{{action}} {{name}}',
    },
    dimmer: {
      stateAria: '{{name}} {{state}}',
      actual: '· Stvarno {{value}}',
      hardwareReports: '· Uređaj javlja {{state}}',
      noState: 'Stanje se ne javlja',
      commanded: '· Naredba poslana, stanje se ne javlja',
      fault: '· Greška',
      levelLabel: 'Razina {{name}}',
      levelReadout: 'Razina {{level}}',
    },
    switch: {
      stateAria: '{{name}} {{state}}',
      locked: 'Zaključano',
      timer: 'Tajmer',
      mismatch: 'Neslaganje',
    },
    temperature: {
      noData: 'nema podataka',
      probeErrorSuffix: ', greška senzora',
      overheatSuffix: ', upozorenje na pregrijavanje',
      overcoolSuffix: ', upozorenje na pothlađivanje',
      aria: '{{name}}: {{value}}{{alertSuffix}}',
      probeError: 'Greška senzora',
      overheat: 'Pregrijavanje',
      overcool: 'Pothlađivanje',
    },
    thermostat: {
      setpoint: 'Zadana temperatura',
      setpointLower: 'zadanu temperaturu',
      mode: 'Način rada',
      fan: 'Ventilator',
      offset: 'Odstupanje',
      offsetLower: 'odstupanje',
      unknown: 'Nepoznato',
      resetOffset: 'Poništi odstupanje',
      windowInterlock: 'Blokada prozora',
      windowOpen: 'Prozor otvoren',
      off: 'Isključeno',
      arrow: '→',
      turnOffAria: 'Isključi {{name}}',
      turnOnAria: 'Uključi {{name}}',
    },
    dashboard: {
      floorCount_one: '{{count}} kat',
      floorCount_few: '{{count}} kata',
      floorCount_other: '{{count}} katova',
      roomCount_one: '{{count}} soba',
      roomCount_few: '{{count}} sobe',
      roomCount_other: '{{count}} soba',
      buildingSummary: '{{floors}} · {{rooms}}',
      unassigned: 'Nedodijeljeno',
      allRooms: 'Sve sobe',
      buildingHierarchyAria: 'Hijerarhija zgrada',
      toggleBuildingAria: 'Uklj./isklj. {{name}}',
      navigate: 'Navigacija',
      roomListAria: 'Popis soba',
      noRoomsHere: 'Nema soba na ovoj lokaciji.',
      buildingsCrumb: 'Zgrade',
      searchRoomsAria: 'Pretraži sobe',
    },
    roomCard: {
      statusActive: 'Aktivno',
      statusSetup: 'Postavljanje',
      statusMaint: 'Održ.',
      statusOff: 'Isključeno',
      alarmActiveSuffix: ', alarm aktivan',
      compactAria: '{{name}} — {{status}}{{alarmSuffix}}',
      residentialAria: '{{name}} — {{channels}}',
      channelCount_one: '{{count}} kanal',
      channelCount_few: '{{count}} kanala',
      channelCount_other: '{{count}} kanala',
      noChannelsMapped: 'Nema mapiranih kanala',
      doNotDisturb: 'Ne ometaj',
      makeUpRoom: 'Počistite sobu',
      offline: 'izvan mreže',
      degrees: '{{value}}°',
      degreesC: '{{value}}°C',
    },
    roomParams: {
      changedCount_one: '{{count}} promijenjen',
      changedCount_few: '{{count}} promijenjena',
      changedCount_other: '{{count}} promijenjeno',
      resetChanges: 'Poništi promjene',
    },
  },
};
